import { FolderStructureType } from '@/contexts/FolderStructureVisibilityContext';

export type LinkEngineItemType = 'glossary' | 'document' | 'blueprint' | 'dialog' | 'flow' | 'variable';

export interface ItemTypeConfig {
  linkEngineType: LinkEngineItemType;
  folderItemType: 'library' | 'documents' | 'blueprints' | 'dialogs' | 'flows' | 'variables';
  dataContextKey: 'glossaryItems' | 'glossaryBlueprints' | 'documents' | 'dialogs' | 'types';
  storePath: string;
  operations: {
    create: string;
    update: string;
    delete: string;
    fetch?: string;
  };
  routes: {
    detail: string;
    new: string;
  };
  specialHandling?: {
    requiresCurrentUser?: boolean;
    requiresDialogKey?: boolean;
    requiresBlueprintVersion?: boolean;
    requiresElementType?: boolean;
    duplicateTranslationKeys?: boolean;
    duplicateDialogKeys?: boolean;
  };
}

export const ITEM_TYPE_CONFIG: Record<FolderStructureType, ItemTypeConfig> = {
  library: {
    linkEngineType: 'glossary',
    folderItemType: 'library',
    dataContextKey: 'glossaryItems',
    storePath: '@/lib/stores/objectsStore',
    operations: {
      create: 'createObject',
      update: 'updateObject',
      delete: 'deleteObject',
      fetch: 'fetchObjects',
    },
    routes: {
      detail: '/library',
      new: '/library/new',
    },
    specialHandling: {
      requiresBlueprintVersion: true,
      duplicateTranslationKeys: true,
    },
  },
  documents: {
    linkEngineType: 'document',
    folderItemType: 'documents',
    dataContextKey: 'documents',
    storePath: '@/lib/stores/documentStore',
    operations: {
      create: 'createDocumentItem',
      update: 'updateDocumentItem',
      delete: 'deleteDocumentItem',
      fetch: 'fetchDocumentItems',
    },
    routes: {
      detail: '/documents',
      new: '/documents/new',
    },
  },
  blueprints: {
    linkEngineType: 'blueprint',
    folderItemType: 'blueprints',
    dataContextKey: 'glossaryBlueprints',
    storePath: '@/lib/stores/templatesStore',
    operations: {
      create: 'createTemplate',
      update: 'updateTemplate',
      delete: 'deleteTemplate',
    },
    routes: {
      detail: '/templates',
      new: '/templates/new',
    },
    specialHandling: {
      requiresBlueprintVersion: true,
      duplicateTranslationKeys: true,
    },
  },
  dialogs: {
    linkEngineType: 'dialog',
    folderItemType: 'dialogs',
    dataContextKey: 'dialogs',
    storePath: '@/lib/stores/dialogStore',
    operations: {
      create: 'createDialog',
      update: 'updateDialog',
      delete: 'deleteDialog',
      fetch: 'fetchDialogs',
    },
    routes: {
      detail: '/dialogs',
      new: '/dialogs/new',
    },
    specialHandling: {
      requiresDialogKey: true,
      duplicateDialogKeys: true,
    },
  },
  flows: {
    linkEngineType: 'flow',
    folderItemType: 'flows',
    storePath: '@/lib/stores/logicFlowStore',
    dataContextKey: 'dialogs', // Note: flows use logicFlowStore.getData() instead of context
    operations: {
      create: 'createLogicFlow',
      update: 'updateLogicFlow',
      delete: 'deleteLogicFlow',
    },
    routes: {
      detail: '/logic',
      new: '/logic/new',
    },
    specialHandling: {
      requiresCurrentUser: true,
    },
  },
  variables: {
    linkEngineType: 'variable',
    folderItemType: 'variables',
    dataContextKey: 'types',
    storePath: '@/lib/stores/typesStore',
    operations: {
      create: 'createType',
      update: 'updateType',
      delete: 'deleteType',
      fetch: 'fetchTypes',
    },
    routes: {
      detail: '/variables',
      new: '/variables/new',
    },
    specialHandling: {
      requiresElementType: true,
    },
  },
};

// Helper function to get link engine item type
export function getLinkEngineItemType(folderType: FolderStructureType): LinkEngineItemType {
  return ITEM_TYPE_CONFIG[folderType].linkEngineType;
}

// Helper function to get folder item type
export function getFolderItemType(folderType: FolderStructureType): 'library' | 'documents' | 'blueprints' | 'dialogs' | 'flows' | 'variables' {
  return ITEM_TYPE_CONFIG[folderType].folderItemType;
}

// Helper to normalize blueprint version
export function normalizeBlueprintVersion(version: any) {
  let normalizedVersion = { changes: {}, version: 0.00001 };
  if (version) {
    if ('changes' in version && 'version' in version) {
      normalizedVersion = version;
    } else if ('major' in version) {
      normalizedVersion = { changes: {}, version: 0.00001 };
    }
  }
  return normalizedVersion;
}
